import SectionWrapper from '../SectionWrapper';
import {
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Link,
  Text,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import {
  clearStampShortValues,
  clearStampLongValues,
  setStampShortValue,
  setStampLongValue,
  syncStampValuesToStampCount,
} from '../../../../store/redux/slices/template.slice';
import { clearNamespaceErrors } from '../../../../store/redux/slices/templateErrors.slice';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InfoOutlineIcon,
} from '@chakra-ui/icons';
import { useState, useEffect, useMemo, useRef } from 'react';
import AddonInput from '../AddonInput';
import type { FieldsState } from '../../../../store/types/template';
import { SLOT_LABELS } from '../utils/fieldVisibilityConfig';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import FieldEditIndicator from '../FieldEditIndicator';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import type { RefObject } from 'react';
import RemoveButton from '../RemoveButton';

const FIELD_SECTIONS = ['header', 'central', 'details', 'back'] as const;

/** Resolve section_slot (e.g. "details_one") to the field id at that position. */
export const getFieldIdBySectionSlot = (
  fields: FieldsState,
  sectionSlot: string
): string | undefined => {
  if (!sectionSlot || !sectionSlot.includes('_')) return undefined;
  const parts = sectionSlot.split('_');
  const slot = parts[parts.length - 1];
  const section = parts.slice(0, -1).join('_');
  if (
    !FIELD_SECTIONS.includes(section as (typeof FIELD_SECTIONS)[number]) ||
    !fields[section as keyof FieldsState]?.[slot]
  ) {
    return undefined;
  }
  const field = fields[section as keyof FieldsState][slot];
  return field && typeof field === 'object' && 'id' in field
    ? field.id
    : undefined;
};

const getFieldDisplayLabel = (fields: FieldsState, fieldId: string): string => {
  for (const section of FIELD_SECTIONS) {
    const sectionFields = fields[section];
    if (!sectionFields) continue;
    for (const [slot, field] of Object.entries(sectionFields)) {
      if (field?.id === fieldId) {
        const sectionLabel = section.charAt(0).toUpperCase() + section.slice(1);
        const slotNumber = SLOT_LABELS[slot] ?? slot;
        return `${sectionLabel} Field ${slotNumber}`;
      }
    }
  }
  return fieldId;
};

const FieldUpdatesSection = () => {
  const stampShortKey = useAppSelector((s) => s.template.stampShortKey);
  const stampLongKey = useAppSelector((s) => s.template.stampLongKey);
  const stampCount = useAppSelector((s) => s.template.stampCount);
  const stampShortValues = useAppSelector((s) => s.template.stampShortValues);
  const stampLongValues = useAppSelector((s) => s.template.stampLongValues);
  const fields = useAppSelector((s) => s.template.fields);

  const stampShortLabel = useMemo(
    () =>
      stampShortKey
        ? getFieldDisplayLabel(
            fields,
            getFieldIdBySectionSlot(fields, stampShortKey) ?? stampShortKey
          )
        : null,
    [fields, stampShortKey]
  );
  const stampLongLabel = useMemo(
    () =>
      stampLongKey
        ? getFieldDisplayLabel(
            fields,
            getFieldIdBySectionSlot(fields, stampLongKey) ?? stampLongKey
          )
        : null,
    [fields, stampLongKey]
  );
  const [isShortOpen, setIsShortOpen] = useState(true);
  const [isLongOpen, setIsLongOpen] = useState(true);

  const dispatch = useAppDispatch();
  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation();
  const errors = useAppSelector((s) => s.templateErrors);

  const shortWrapperRefs = useRef<Map<string, RefObject<HTMLDivElement>>>(
    new Map()
  );
  const longWrapperRefs = useRef<Map<string, RefObject<HTMLDivElement>>>(
    new Map()
  );

  const getOrCreateShortRef = (key: string): RefObject<HTMLDivElement> => {
    let ref = shortWrapperRefs.current.get(key);
    if (!ref) {
      ref = { current: null };
      shortWrapperRefs.current.set(key, ref);
    }
    return ref;
  };
  const getOrCreateLongRef = (key: string): RefObject<HTMLDivElement> => {
    let ref = longWrapperRefs.current.get(key);
    if (!ref) {
      ref = { current: null };
      longWrapperRefs.current.set(key, ref);
    }
    return ref;
  };

  const shortFieldRefMap = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(stampShortValues).map((k) => [
          `stampShortValues.${k}`,
          getOrCreateShortRef(k),
        ])
      ),
    [stampShortValues]
  );
  const longFieldRefMap = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(stampLongValues).map((k) => [
          `stampLongValues.${k}`,
          getOrCreateLongRef(k),
        ])
      ),
    [stampLongValues]
  );

  const {
    activeFieldRef: activeShortRef,
    isFieldBeingEdited: isShortBeingEdited,
  } = useFieldEditIndicator({ fieldRefMap: shortFieldRefMap });

  const {
    activeFieldRef: activeLongRef,
    isFieldBeingEdited: isLongBeingEdited,
  } = useFieldEditIndicator({ fieldRefMap: longFieldRefMap });

  useEffect(() => {
    dispatch(syncStampValuesToStampCount());
  }, [dispatch, stampCount]);

  const handleStampShortChange = (key: string, value: string) => {
    validateField(`stampShortValues.${key}`, value, templateState);
    dispatch(setStampShortValue({ key, value }));
  };

  const handleStampLongChange = (key: string, value: string) => {
    validateField(`stampLongValues.${key}`, value, templateState);
    dispatch(setStampLongValue({ key, value }));
  };

  const hasAnyShortValue = useMemo(
    () =>
      Object.values(stampShortValues).some(
        (v) => v != null && String(v).trim() !== ''
      ),
    [stampShortValues]
  );

  const hasAnyLongValue = useMemo(
    () =>
      Object.values(stampLongValues).some(
        (v) => v != null && String(v).trim() !== ''
      ),
    [stampLongValues]
  );

  return (
    <SectionWrapper
      id="field-updates-section"
      title="Field updates by stamp count"
      subtitle="Update pass field(s) default value as stamps are earned."
      openOnLoad={true}
    >
      <Flex direction="column" gap={4}>
        {!stampShortKey && !stampLongKey && (
          <Text>
            You'll need to{' '}
            <Button
              variant="link"
              onClick={() => {
                const element = document.getElementById('fields-section');
                if (element) {
                  const yOffset = -32;
                  const targetY =
                    element.getBoundingClientRect().top +
                    window.scrollY +
                    yOffset;
                  const startY = window.scrollY;
                  const startTime = performance.now();
                  const duration = 500;

                  const step = (now: number) => {
                    const elapsed = now - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const ease = 0.5 - 0.5 * Math.cos(progress * Math.PI);
                    window.scrollTo(0, startY + (targetY - startY) * ease);
                    if (progress < 1) {
                      requestAnimationFrame(step);
                    }
                  };
                  requestAnimationFrame(step);
                }
              }}
            >
              select pass field(s)
            </Button>{' '}
            to update before adding stage-specific text.
          </Text>
        )}
        {(stampShortKey || stampLongKey) && (
          <Flex
            width="100%"
            alignItems="center"
            gap={2}
            borderRadius="5px"
            borderColor="lightGrey"
            bg="#E5F6FA"
            px={3}
            py={2}
          >
            <InfoOutlineIcon color="#3182CE" mt="2px" aria-hidden />
            <Text textStyle="bodyFamilyRegular" fontSize="12px" color="#4C4C4C">
              Any default field values will be displayed when a user first adds
              the pass to their wallet. If no default is set, the unstamped
              value will be shown.
            </Text>
          </Flex>
        )}
        {stampShortKey && (
          <Box
            p={3}
            border="1px solid"
            borderColor="primaryBackground"
            borderRadius="md"
            mb={3}
            position="relative"
          >
            <Flex
              alignItems="center"
              justify="space-between"
              cursor="pointer"
              onClick={() => setIsShortOpen(!isShortOpen)}
            >
              <FormLabel variant="templateBuilderLabel" m={0} fontWeight="500">
                {stampShortLabel} |{' '}
                {getFieldIdBySectionSlot(fields, stampShortKey) ??
                  stampShortKey}
              </FormLabel>
              {isShortOpen ? (
                <ChevronUpIcon boxSize="20px" />
              ) : (
                <ChevronDownIcon boxSize="20px" />
              )}
            </Flex>
            <Collapse in={isShortOpen}>
              <Flex direction="column" gap={4} mt={4}>
                {Object.entries(stampShortValues).map(([key, value], index) => (
                  <FieldEditIndicator
                    key={key}
                    fieldRef={getOrCreateShortRef(key)}
                    isFieldBeingEdited={isShortBeingEdited}
                    activeFieldRef={activeShortRef}
                    left="-55px"
                    top="50%"
                    show={
                      isShortBeingEdited &&
                      activeShortRef === getOrCreateShortRef(key)
                    }
                  >
                    <FormControl
                      isRequired
                      isInvalid={!!errors[`stampShortValues.${key}`]}
                    >
                      <AddonInput
                        label={index === 0 ? 'Unstamped' : `Stamp ${index}`}
                        value={value as string}
                        onChange={(value) => handleStampShortChange(key, value)}
                        placeholder={`${index === 1 ? `e.g. Only ${stampCount - index} stamp to go!` : ''}`}
                        onFocus={() => handleFocus(`stampShortValues.${key}`)}
                        onBlur={handleBlur}
                        isRequired
                      />
                      <FormErrorMessage
                        pl={2}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                      >
                        <CustomErrorIcon />
                        {errors[`stampShortValues.${key}`]}
                      </FormErrorMessage>
                    </FormControl>
                  </FieldEditIndicator>
                ))}
              </Flex>
            </Collapse>
            {isShortOpen && (
              <Box mt={4}>
                <RemoveButton
                  label="Remove all"
                  isDisabled={!hasAnyShortValue}
                  onClick={() => {
                    dispatch(clearStampShortValues());
                    dispatch(clearNamespaceErrors('stampShortValues'));
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        {stampLongKey && (
          <Box
            p={3}
            border="1px solid"
            borderColor="primaryBackground"
            borderRadius="md"
            mb={3}
            position="relative"
          >
            <Flex
              alignItems="center"
              justify="space-between"
              cursor="pointer"
              onClick={() => setIsLongOpen(!isLongOpen)}
            >
              <FormLabel variant="templateBuilderLabel" m={0} fontWeight="500">
                {stampLongLabel} |{' '}
                {getFieldIdBySectionSlot(fields, stampLongKey) ?? stampLongKey}
              </FormLabel>
              {isLongOpen ? (
                <ChevronUpIcon boxSize="20px" />
              ) : (
                <ChevronDownIcon boxSize="20px" />
              )}
            </Flex>
            <Collapse in={isLongOpen}>
              <Flex direction="column" gap={4} mt={4}>
                {Object.entries(stampLongValues).map(([key, value], index) => (
                  <FieldEditIndicator
                    key={key}
                    fieldRef={getOrCreateLongRef(key)}
                    isFieldBeingEdited={isLongBeingEdited}
                    activeFieldRef={activeLongRef}
                    left="-55px"
                    top="50%"
                    show={
                      isLongBeingEdited &&
                      activeLongRef === getOrCreateLongRef(key)
                    }
                  >
                    <FormControl
                      isRequired
                      isInvalid={!!errors[`stampLongValues.${key}`]}
                    >
                      <AddonInput
                        label={index === 0 ? 'Unstamped' : `Stamp ${index}`}
                        value={value as string}
                        onChange={(value) => handleStampLongChange(key, value)}
                        placeholder={`${index === 1 ? `e.g. Only ${stampCount - index} stamp to go!` : ''}`}
                        onFocus={() => handleFocus(`stampLongValues.${key}`)}
                        onBlur={handleBlur}
                        isRequired={true}
                      />
                      <FormErrorMessage
                        pl={2}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                      >
                        <CustomErrorIcon />
                        {errors[`stampLongValues.${key}`]}
                      </FormErrorMessage>
                    </FormControl>
                  </FieldEditIndicator>
                ))}
              </Flex>
            </Collapse>
            {isLongOpen && (
              <Box mt={4}>
                <RemoveButton
                  label="Remove all"
                  isDisabled={!hasAnyLongValue}
                  onClick={() => {
                    dispatch(clearStampLongValues());
                    dispatch(clearNamespaceErrors('stampLongValues'));
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Flex>
    </SectionWrapper>
  );
};

export default FieldUpdatesSection;
